/** Small shared components. */
import type { ComponentChildren } from 'preact';
import type { Source, Verification } from '../core/types';

export function Tie({ verified, onToggle, title }: { verified: boolean; onToggle?: () => void; title?: string }) {
  const t = title ?? (verified ? 'Verified — click to mark unverified' : 'Not verified — click to mark verified on the ground');
  if (!onToggle) return <span class={`tie ${verified ? 'tie-v' : ''}`} role="img" aria-label={verified ? 'verified' : 'not verified'} />;
  return (
    <button
      type="button" onClick={onToggle} title={t} aria-pressed={verified}
      style="background:none;border:none;cursor:pointer;padding:6px;line-height:0;"
    >
      <span class={`tie ${verified ? 'tie-v' : ''}`} />
    </button>
  );
}

export function FlagLine({ items }: { items: Verification[] }) {
  const n = items.filter((v) => v.verified).length;
  const shown = items.slice(0, 40);
  return (
    <span style="display:inline-flex;align-items:center;gap:10px;">
      <span class="verify-count num">{n}<span class="of"> / {items.length} verified</span></span>
      <span class="flagline" role="img" aria-label={`${n} of ${items.length} lines verified`}>
        {shown.map((v, i) => <span key={i} class={`tie ${v.verified ? 'tie-v' : ''}`} />)}
        {items.length > shown.length && <span class="small muted num">+{items.length - shown.length}</span>}
      </span>
    </span>
  );
}

export function SourceChip({ source }: { source: Source }) {
  const label = source === 'manual' ? 'hand' : source;
  return <span class={`srcchip ${source === 'ai' ? 'srcchip-ai' : ''}`}>{label}</span>;
}

export function Empty({ children }: { children: ComponentChildren }) {
  return <div class="empty">{children}</div>;
}

export function Banner({ kind = 'error', children, onClose }: { kind?: 'error' | 'info'; children: ComponentChildren; onClose?: () => void }) {
  return (
    <div class={`banner ${kind === 'info' ? 'banner-info' : ''}`} role={kind === 'error' ? 'alert' : 'status'}>
      <div>{children}</div>
      {onClose && <button class="btn btn-small btn-ghost" onClick={onClose}>OK</button>}
    </div>
  );
}

export const STATUS_LABELS: Record<string, string> = {
  lead: 'Lead', bid_sent: 'Bid sent', won: 'Won', lost: 'Lost',
  in_progress: 'In progress', complete: 'Complete',
};
